import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Zap, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'og_flash_floating_dismissed_at';

/**
 * Bottom-center sticky pill that surfaces an active flash sale anywhere on
 * the storefront. Hides itself for 30 minutes if the user X's it (we don't
 * want to fight them after they've declined). Auto-unmounts when no sale
 * is live, so the home strip + floating CTA never appear simultaneously
 * when there's nothing to advertise.
 */
export default function FlashSaleFloatingCTA() {
  const { pathname } = useLocation();
  const HIDE_ON = ['/admin', '/admin-portal', '/pos', '/checkout', '/ogquick', '/chat'];
  const [items, setItems] = useState([]);
  const [, setTick] = useState(0);
  const [hidden, setHidden] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/products?flash_sale=true&limit=1`);
      const arr = Array.isArray(data?.products) ? data.products : (data || []);
      setItems(arr);
    } catch { /* silent */ }
  };

  useEffect(() => {
    // Honour a recent dismissal (30 min cool-down).
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < 30 * 60 * 1000) setHidden(true);
    refresh();
    const r = setInterval(refresh, 60000);
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => { clearInterval(r); clearInterval(t); };
  }, []);

  if (HIDE_ON.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return null;
  if (hidden) return null;

  const live = items.filter((p) => p.flash_sale_until && new Date(p.flash_sale_until).getTime() > Date.now());
  if (live.length === 0) return null;

  const remainMs = new Date(live[0].flash_sale_until).getTime() - Date.now();
  const h = Math.floor(remainMs / 3600000);
  const m = Math.floor((remainMs % 3600000) / 60000);
  const s = Math.floor((remainMs % 60000) / 1000);

  const dismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setHidden(true);
  };

  return (
    <Link
      to="/shop?flash=1"
      data-testid="flash-floating-cta"
      className="fixed left-1/2 -translate-x-1/2 z-40 group flex items-center gap-3 bg-gradient-to-r from-[#FF2A2A] via-[#FF4D4D] to-[#FF7A2A] text-white rounded-full pl-4 pr-3 py-2 shadow-2xl hover:shadow-[0_8px_40px_rgba(255,42,42,0.4)] transition-all hover:scale-[1.03]"
      // Sit just above the bottom-right OGquick bubble + bottom-left WhatsApp
      // button without colliding with either.
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 92px)' }}
    >
      <span className="relative flex items-center justify-center w-7 h-7 bg-white/20 rounded-full">
        <Zap size={14} className="text-white animate-pulse" />
      </span>
      <span className="text-[11px] sm:text-xs font-bold leading-tight">
        ⚡ Flash Sale Live
        <span className="block text-[10px] opacity-90 font-mono tabular-nums">
          Ends in {String(h).padStart(2, '0')}:{String(m).padStart(2, '0')}:{String(s).padStart(2, '0')}
        </span>
      </span>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss flash sale banner"
        data-testid="flash-floating-cta-close"
        className="ml-1 w-6 h-6 rounded-full hover:bg-white/15 flex items-center justify-center">
        <X size={13} />
      </button>
    </Link>
  );
}
